import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Pencil, Plus, Trash2, RefreshCw, Search } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function AirlinesData() {
  const [airlineCodes, setAirlineCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [confirmUpdateDialog, setConfirmUpdateDialog] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [formData, setFormData] = useState({ code: '', airline_name: '' });
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [showOnlyUsed, setShowOnlyUsed] = useState(false);
  const [flightsDialogOpen, setFlightsDialogOpen] = useState(false);
  const [selectedAirlineFlights, setSelectedAirlineFlights] = useState([]);
  const [selectedAirlineCode, setSelectedAirlineCode] = useState('');
  const [loadingFlights, setLoadingFlights] = useState(false);
  const { toast } = useToast();

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchAirlineCodes();
  }, [filterText]);

  const fetchAirlineCodes = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const params = filterText ? { filter: filterText } : {};
      const response = await axios.get(`${API}/api/airline-codes`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setAirlineCodes(response.data);
      setLoading(false);
      setRefreshing(false);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to fetch airline codes:', error);
      toast({
        title: "Error",
        description: "Failed to fetch airline codes",
        variant: "destructive"
      });
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchAirlineCodes(true);
    toast({
      title: "Refreshing",
      description: "Updating flight counts..."
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/airline-codes`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Success",
        description: `Airline code ${formData.code} created successfully`
      });
      setCreateDialogOpen(false);
      setFormData({ code: '', airline_name: '' });
      fetchAirlineCodes();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create airline code",
        variant: "destructive"
      });
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/airline-codes/${selectedCode.code}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Success",
        description: `Airline code ${selectedCode.code} updated successfully`
      });
      setEditDialogOpen(false);
      setSelectedCode(null);
      setFormData({ code: '', airline_name: '' });
      fetchAirlineCodes();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update airline code",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Are you sure you want to delete airline code "${code}"?`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/airline-codes/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Success",
        description: `Airline code ${code} deleted successfully`
      });
      fetchAirlineCodes();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete airline code",
        variant: "destructive"
      });
    }
  };

  const handleUpdateAllFlights = async () => {
    setConfirmUpdateDialog(false);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/airline-codes/update-flights`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Success",
        description: response.data.message
      });
      fetchAirlineCodes();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update flights",
        variant: "destructive"
      });
    }
  };

  const openEditDialog = (code) => {
    setSelectedCode(code);
    setFormData({ code: code.code, airline_name: code.airline_name });
    setEditDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg" style={{ color: '#E8E8E8' }}>Loading...</div>
      </div>
    );
  }

  const unidentifiedCount = airlineCodes.filter(code => code.airline_name === "❌ Not Identified").length;
  const identifiedCount = airlineCodes.length - unidentifiedCount;
  const totalFlights = airlineCodes.reduce((sum, code) => sum + code.used_flights_count, 0);
  const activeAirlines = airlineCodes.filter(code => code.used_flights_count > 0).length;

  // Filter codes based on showOnlyUsed toggle
  const displayedCodes = showOnlyUsed 
    ? airlineCodes.filter(code => code.used_flights_count > 0)
    : airlineCodes;

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const handleViewFlights = async (airlineCode) => {
    setSelectedAirlineCode(airlineCode);
    setLoadingFlights(true);
    setFlightsDialogOpen(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/flights`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter flights by prefix
      const flights = response.data.filter(flight => flight.flight_prefix === airlineCode);
      setSelectedAirlineFlights(flights);
      setLoadingFlights(false);
    } catch (error) {
      console.error('Failed to fetch flights:', error);
      toast({
        title: "Error",
        description: "Failed to fetch flights",
        variant: "destructive"
      });
      setLoadingFlights(false);
    }
  };

  const handleEditFlightFromList = (flightId) => {
    // Navigate to flights page with edit mode
    window.location.href = `/flights?edit=${flightId}`;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2" style={{ color: '#FFD700' }}>Airlines Data Management</h1>
        <p style={{ color: '#B0B0B0' }}>Manage airline codes and prefixes</p>
      </div>

      {/* Statistics Panel */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold" style={{ color: '#22C55E' }}>{identifiedCount}</div>
              <div className="text-sm" style={{ color: '#B0B0B0' }}>Identified Airlines</div>
            </div>
          </CardContent>
        </Card>
        <Card style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold" style={{ color: '#EF4444' }}>{unidentifiedCount}</div>
              <div className="text-sm" style={{ color: '#B0B0B0' }}>Unidentified Prefixes</div>
            </div>
          </CardContent>
        </Card>
        <Card style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold" style={{ color: '#3B82F6' }}>{activeAirlines}</div>
              <div className="text-sm" style={{ color: '#B0B0B0' }}>Airlines with Flights</div>
            </div>
          </CardContent>
        </Card>
        <Card style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold" style={{ color: '#FFA500' }}>{totalFlights}</div>
              <div className="text-sm" style={{ color: '#B0B0B0' }}>Total Flights</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 mb-6 items-center">
        <Button 
          onClick={() => {
            setFormData({ code: '', airline_name: '' });
            setCreateDialogOpen(true);
          }}
          style={{ background: '#22C55E', color: '#FFFFFF' }}
        >
          <Plus className="mr-2 h-4 w-4" />
          Create New Prefix/Airline
        </Button>
        <Button 
          onClick={() => setConfirmUpdateDialog(true)}
          style={{ background: '#3B82F6', color: '#FFFFFF' }}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Update Database
        </Button>
        <Button 
          onClick={handleRefresh}
          disabled={refreshing}
          style={{ background: '#6366F1', color: '#FFFFFF' }}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh Counts'}
        </Button>
        <div className="ml-auto text-sm" style={{ color: '#B0B0B0' }}>
          Last updated: {formatTime(lastRefresh)}
        </div>
      </div>

      {/* Filter */}
      <div className="mb-6 flex gap-4 items-center">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <Search className="h-5 w-5" style={{ color: '#B0B0B0' }} />
          <Input
            placeholder="Filter by Prefix or Airline Name..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
          />
        </div>
        <Button
          onClick={() => setShowOnlyUsed(!showOnlyUsed)}
          variant={showOnlyUsed ? "default" : "outline"}
          style={{ 
            background: showOnlyUsed ? '#3B82F6' : 'transparent',
            color: showOnlyUsed ? '#FFFFFF' : '#E8E8E8',
            borderColor: '#4A5A8A'
          }}
        >
          {showOnlyUsed ? '✓ ' : ''}Show Only Used ({activeAirlines})
        </Button>
      </div>

      {/* Summary Stats */}
      {unidentifiedCount > 0 && (
        <div className="mb-6 p-4 rounded" style={{ background: '#3D1F1F', borderColor: '#EF4444', border: '1px solid' }}>
          <div className="flex items-center gap-2">
            <span style={{ color: '#EF4444', fontSize: '20px' }}>⚠️</span>
            <span style={{ color: '#E8E8E8' }}>
              <strong style={{ color: '#FFA500' }}>{unidentifiedCount}</strong> unidentified prefix{unidentifiedCount > 1 ? 'es' : ''} found with active flights. 
              Please assign airline names to these prefixes.
            </span>
          </div>
        </div>
      )}

      {/* Airlines Table */}
      <Card style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
        <CardHeader>
          <CardTitle style={{ color: '#FFFFFF' }}>
            Airline Codes ({displayedCodes.length} {showOnlyUsed ? 'used' : 'total'})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid #4A5A8A' }}>
                  <th className="text-left py-3 px-4" style={{ color: '#FFD700' }}>Prefix</th>
                  <th className="text-left py-3 px-4" style={{ color: '#FFD700' }}>Airline Name</th>
                  <th className="text-center py-3 px-4" style={{ color: '#FFD700' }}>Used Flights</th>
                  <th className="text-center py-3 px-4" style={{ color: '#FFD700' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedCodes.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-8" style={{ color: '#B0B0B0' }}>
                      {showOnlyUsed ? 'No airlines with active flights' : 'No airline codes found'}
                    </td>
                  </tr>
                ) : (
                  displayedCodes.map((code) => {
                    const isUnidentified = code.airline_name === "❌ Not Identified";
                    return (
                      <tr 
                        key={code.code} 
                        style={{ 
                          borderBottom: '1px solid #2A3A5A',
                          background: isUnidentified ? '#3D1F1F' : 'transparent'
                        }}
                      >
                        <td className="py-3 px-4" style={{ color: '#E8E8E8', fontWeight: 'bold' }}>
                          {code.code}
                        </td>
                        <td className="py-3 px-4" style={{ 
                          color: isUnidentified ? '#EF4444' : '#E8E8E8',
                          fontWeight: isUnidentified ? 'bold' : 'normal'
                        }}>
                          {code.airline_name}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {code.used_flights_count > 0 ? (
                            <button
                              onClick={() => handleViewFlights(code.code)}
                              className="hover:underline cursor-pointer"
                              style={{ 
                                color: isUnidentified ? '#FFA500' : '#3B82F6', 
                                fontWeight: 'bold',
                                background: 'none',
                                border: 'none'
                              }}
                            >
                              {code.used_flights_count}
                            </button>
                          ) : (
                            <span style={{ color: '#808080' }}>0</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => openEditDialog(code)}
                              style={{ background: '#3B82F6', color: '#FFFFFF' }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleDelete(code.code)}
                              style={{ background: '#EF4444', color: '#FFFFFF' }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFD700' }}>Create New Airline Code</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              Add a new airline prefix and name
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label style={{ color: '#E8E8E8' }}>Prefix *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., 5O, SN"
                maxLength="3"
                required
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
              />
              <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>2-3 characters</p>
            </div>
            <div>
              <Label style={{ color: '#E8E8E8' }}>Airline Name *</Label>
              <Input
                value={formData.airline_name}
                onChange={(e) => setFormData({ ...formData, airline_name: e.target.value })}
                placeholder="e.g., Europe Airpost"
                required
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" onClick={() => setCreateDialogOpen(false)} variant="outline">
                Cancel
              </Button>
              <Button type="submit" style={{ background: '#22C55E', color: '#FFFFFF' }}>
                Create
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFD700' }}>Edit Airline Code</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              Update prefix and airline name
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div>
              <Label style={{ color: '#E8E8E8' }}>Prefix *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                maxLength="3"
                required
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
              />
              <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>2-3 characters (e.g., AT, SN, EK)</p>
            </div>
            <div>
              <Label style={{ color: '#E8E8E8' }}>Airline Name *</Label>
              <Input
                value={formData.airline_name}
                onChange={(e) => setFormData({ ...formData, airline_name: e.target.value })}
                placeholder="e.g., Austrian Airlines"
                required
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" onClick={() => setEditDialogOpen(false)} variant="outline">
                Cancel
              </Button>
              <Button type="submit" style={{ background: '#3B82F6', color: '#FFFFFF' }}>
                Update
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm Update All Flights Dialog */}
      <Dialog open={confirmUpdateDialog} onOpenChange={setConfirmUpdateDialog}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFD700' }}>⚠️ Update All Flights</DialogTitle>
            <DialogDescription style={{ color: '#E8E8E8' }}>
              This will update all existing flights with the latest airline data based on their flight prefix.
              <br /><br />
              <strong>Are you sure you want to continue?</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setConfirmUpdateDialog(false)} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleUpdateAllFlights} style={{ background: '#3B82F6', color: '#FFFFFF' }}>
              Yes, Update Database
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Flights List Dialog */}
      <Dialog open={flightsDialogOpen} onOpenChange={setFlightsDialogOpen}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A', maxWidth: '900px', width: '90%' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFD700' }}>
              ✈️ Flights using prefix "{selectedAirlineCode}"
            </DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              {selectedAirlineFlights.length} flight{selectedAirlineFlights.length !== 1 ? 's' : ''} found
            </DialogDescription>
          </DialogHeader>
          
          {loadingFlights ? (
            <div className="text-center py-8" style={{ color: '#E8E8E8' }}>Loading flights...</div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead style={{ position: 'sticky', top: 0, background: '#1E2A4A', borderBottom: '2px solid #4A5A8A' }}>
                  <tr>
                    <th className="text-left py-2 px-3" style={{ color: '#FFD700' }}>Date</th>
                    <th className="text-left py-2 px-3" style={{ color: '#FFD700' }}>Flight</th>
                    <th className="text-left py-2 px-3" style={{ color: '#FFD700' }}>Route</th>
                    <th className="text-left py-2 px-3" style={{ color: '#FFD700' }}>Time</th>
                    <th className="text-center py-2 px-3" style={{ color: '#FFD700' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedAirlineFlights.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-8" style={{ color: '#B0B0B0' }}>
                        No flights found
                      </td>
                    </tr>
                  ) : (
                    selectedAirlineFlights.map((flight) => (
                      <tr key={flight.id} style={{ borderBottom: '1px solid #2A3A5A' }}>
                        <td className="py-2 px-3" style={{ color: '#E8E8E8' }}>
                          {flight.flight_date || 'N/A'}
                        </td>
                        <td className="py-2 px-3" style={{ color: '#3B82F6', fontWeight: 'bold' }}>
                          {flight.flight_number || `${flight.flight_prefix}${flight.flight_number_only}`}
                        </td>
                        <td className="py-2 px-3" style={{ color: '#E8E8E8', fontSize: '0.875rem' }}>
                          {flight.departure_airport || 'N/A'} → {flight.arrival_airport || 'N/A'}
                        </td>
                        <td className="py-2 px-3" style={{ color: '#E8E8E8' }}>
                          {flight.flight_time || 'N/A'}
                        </td>
                        <td className="py-2 px-3 text-center">
                          <Button
                            size="sm"
                            onClick={() => handleEditFlightFromList(flight.id)}
                            style={{ background: '#3B82F6', color: '#FFFFFF' }}
                          >
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
          
          <div className="flex justify-end mt-4">
            <Button onClick={() => setFlightsDialogOpen(false)} variant="outline">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
